import { Link, useParams } from 'react-router-dom';
import CaseStudyCard from '../components/CaseStudyCard';
import Layout from '../components/Layout';
import PageHeader from '../components/PageHeader';
import SectionHeader from '../components/SectionHeader';
import ServiceCard from '../components/ServiceCard';
import ToolLogo from '../components/ToolLogo';
import { Icon } from '../components/icons';
import { CaseStudy, getCaseStudy } from '../data/caseStudies';
import { SERVICE_CARDS, getService } from '../data/services';
import { usePageMeta } from '../hooks/usePageMeta';
import NotFound from './NotFound';

function ServiceDetail() {
  const { slug } = useParams<{ slug: string }>();
  const service = getService(slug);

  usePageMeta({
    title: service ? `${service.title} | EightyThreeLabs` : 'Service Not Found | EightyThreeLabs',
    description: service?.summary,
  });

  if (!service) {
    return <NotFound />;
  }

  const related = SERVICE_CARDS.filter(s => s.slug !== service.slug);
  const caseStudies = (service.caseStudyIds ?? [])
    .map(getCaseStudy)
    .filter((c): c is CaseStudy => Boolean(c));

  // Sections after "03 Process" (which is gray) are optional, so alternate
  // white/gray based on which ones actually render rather than hard-coding.
  const tail = [
    service.outcomes ? 'outcomes' : null,
    caseStudies.length > 0 ? 'cases' : null,
    'stack',
    'related',
  ].filter((s): s is string => s !== null);
  const bg = (key: string) => (tail.indexOf(key) % 2 === 0 ? 'section-white' : 'section-gray');

  return (
    <Layout>
      <PageHeader
        eyebrow={service.shortTitle.toUpperCase()}
        title={service.title}
        description={service.description}
      />

      <section className="section section-white">
        <div className="container">
          <div className="svc-challenge reveal">
            <h3>{service.challenge.title}</h3>
            <p>{service.challenge.body}</p>
          </div>
        </div>
      </section>

      <section className="section section-gray">
        <div className="container">
          <SectionHeader
            number="01"
            eyebrow="KEY CAPABILITIES"
            title="What This Covers"
            align="left"
          />
          <div className="svc-capabilities-grid reveal">
            {service.capabilities.map(item => (
              <div className="svc-capability-item" key={item}>
                <span className="svc-capability-check">
                  <Icon id="check" size={16} />
                </span>
                <span>{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section section-white">
        <div className="container">
          <SectionHeader
            number="02"
            eyebrow="WHAT WE DELIVER"
            title="Outcomes You Can Point To"
            align="left"
          />
          <div className="services-grid reveal">
            {service.deliverables.map(item => (
              <div className="service-card" key={item.title}>
                <div className={`service-icon service-icon-${service.accent}`}>
                  <Icon id={item.icon} size={28} />
                </div>
                <h3 className="service-title">{item.title}</h3>
                <p className="service-description">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section section-gray">
        <div className="container">
          <SectionHeader number="03" eyebrow="OUR PROCESS" title="How We Get There" align="left" />
          <div
            className="approach-grid reveal"
            style={{ gridTemplateColumns: `repeat(${service.process.length}, 1fr)` }}
          >
            {service.process.map((step, i) => (
              <div className="approach-step" key={step.title}>
                <div className="approach-number">{String(i + 1).padStart(2, '0')}</div>
                <h3 className="approach-title">{step.title}</h3>
                <p className="approach-desc">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {service.outcomes && (
        <section className={`section ${bg('outcomes')}`}>
          <div className="container">
            <div className="about-stats about-stats-row reveal">
              {service.outcomes.map(outcome => (
                <div className="stat-card" key={outcome.label}>
                  <div className="stat-number">{outcome.value}</div>
                  <div className="stat-label">{outcome.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {caseStudies.length > 0 && (
        <section className={`section ${bg('cases')}`}>
          <div className="container">
            <SectionHeader
              eyebrow="SELECTED RESULTS"
              title="What this looks like in practice"
              subtitle="An anonymized engagement — the shape of the problem, what we built, and what changed."
              align="left"
            />
            <div className="case-grid case-grid-narrow reveal">
              {caseStudies.map(study => (
                <CaseStudyCard key={study.id} study={study} />
              ))}
            </div>
          </div>
        </section>
      )}

      <section className={`section ${bg('stack')}`}>
        <div className="container">
          <SectionHeader
            number="04"
            eyebrow="THE STACK BEHIND THE WORK"
            title="Built On"
            align="left"
          />
          <div className="stack-row-tools svc-tools-row reveal">
            {service.tools.map(toolId => (
              <ToolLogo key={toolId} toolId={toolId} size={48} />
            ))}
          </div>
        </div>
      </section>

      <section className={`section ${bg('related')}`}>
        <div className="container">
          <SectionHeader eyebrow="RELATED SERVICES" title="Explore More" />
          <div className="services-grid services-grid-2 reveal">
            {related.map(s => (
              <ServiceCard key={s.title} service={s} />
            ))}
          </div>

          <div className="svc-cta reveal">
            <h3>Ready to get started with {service.shortTitle}?</h3>
            <Link to="/contact" className="btn-primary">
              Contact Us About This
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}

export default ServiceDetail;
